import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, MonitorSmartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { useAddNote, useCompanyContacts, useCreateTicket, useMe, usePicklists } from '@/api/hooks';
import { useRmmTicketContext, type RmmDeviceDetail, type RmmTicketContext } from '@/api/rmm';
import { avStatusMeta, deviceClientName, patchStatusMeta } from '@/lib/rmm-helpers';
import { ticketPath } from '@/lib/routes';

/**
 * "Log ticket" from an RMM device — a curated New Ticket modal with everything
 * Cosmos can infer pre-filled: company (site link), configuration item (active
 * Autotask CI for the device) and contact (CI contact → Azure email → username).
 *
 * The ticket goes through the normal POST /api/tickets; the device snapshot is
 * then posted as an INTERNAL note, because the description can be customer-visible.
 */

const SUPPORT_QUEUE_ID = 29682833;
const STATUS_NEW = 1;

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export function LogDeviceTicketDialog({
  open, onOpenChange, detail, prefetch = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: RmmDeviceDetail;
  /** Start matching before the modal opens (e.g. on button hover) so it opens pre-filled. */
  prefetch?: boolean;
}) {
  const ctx = useRmmTicketContext(detail.device.uid, open || prefetch);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Log ticket</DialogTitle>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MonitorSmartphone className="h-3.5 w-3.5" />
            {detail.device.hostname} · {deviceClientName(detail.device)}
          </div>
        </DialogHeader>
        {ctx.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-6 pb-8 pt-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Matching client, contact and configuration item…
          </div>
        ) : (
          // Mounted fresh each time the modal opens, so state initialises from the matched context.
          <LogTicketForm
            detail={detail}
            ctx={ctx.data ?? { company: null, configurationItem: null, contact: null }}
            matchFailed={!!ctx.error}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function LogTicketForm({
  detail, ctx, matchFailed, onDone,
}: {
  detail: RmmDeviceDetail;
  ctx: RmmTicketContext;
  matchFailed: boolean;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const picklists = usePicklists();
  const { data: me } = useMe();
  const createTicket = useCreateTicket();
  const addNote = useAddNote();

  const [company, setCompany] = useState<CompanyOption | null>(ctx.company);
  const [contactId, setContactId] = useState(ctx.contact ? String(ctx.contact.id) : '');
  const [linkCi, setLinkCi] = useState(!!ctx.configurationItem);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [queueId, setQueueId] = useState(String(SUPPORT_QUEUE_ID));
  const [issueType, setIssueType] = useState('');
  const [subIssueType, setSubIssueType] = useState('');
  const [assignToMe, setAssignToMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const contacts = useCompanyContacts(company?.id ?? null);
  const pl = picklists.data;
  const byLabel = (list: { value: string; label: string }[] | undefined, label: string) =>
    list?.find((o) => o.label.toLowerCase() === label)?.value;

  // Hidden defaults, resolved from picklists by label (ids differ per Autotask zone).
  const sourceId = byLabel(pl?.source, 'agent');
  const ticketTypeId = byLabel(pl?.ticketType, 'incident');
  const effectivePriority = priority || byLabel(pl?.priority, 'medium') || '';

  // The CI and matched contact only make sense for the matched company.
  const sameCompany = company?.id === ctx.company?.id;
  const ci = sameCompany ? ctx.configurationItem : null;
  const selectedContact = contacts.data?.items.find((c) => String(c.id) === contactId);
  const subIssueOpts = pl?.subIssueType.filter((o) => String(o.parentValue) === issueType) ?? [];

  const canSubmit = !!title.trim() && !!company && !!effectivePriority && !submitting;

  function changeCompany(next: CompanyOption | null) {
    setCompany(next);
    setContactId(next?.id === ctx.company?.id && ctx.contact ? String(ctx.contact.id) : '');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !company) return;
    setSubmitting(true);

    const fields: Record<string, unknown> = {
      title: title.trim(),
      companyID: company.id,
      status: STATUS_NEW,
      priority: Number(effectivePriority),
      queueID: Number(queueId),
    };
    if (description.trim()) fields.description = description.trim();
    if (contactId) fields.contactID = Number(contactId);
    if (ci && linkCi) fields.configurationItemID = ci.id;
    if (sourceId) fields.source = Number(sourceId);
    if (ticketTypeId) fields.ticketType = Number(ticketTypeId);
    if (issueType) fields.issueType = Number(issueType);
    if (subIssueType) fields.subIssueType = Number(subIssueType);
    if (assignToMe && me?.item?.id) fields.assignedResourceID = me.item.id;

    let id: number | undefined;
    try {
      const data = await createTicket.mutateAsync(fields);
      id = (data as any)?.itemId ?? (data as any)?.item?.id ?? (data as any)?.id; // Autotask returns { itemId }
    } catch (err) {
      setSubmitting(false);
      toast.error('Failed to create ticket', { description: err instanceof Error ? err.message : 'Unknown error' });
      return;
    }

    // The ticket is the record that must land; a failed snapshot note is only a warning.
    let noteFailed = false;
    if (id != null) {
      try {
        await addNote.mutateAsync({ id, body: { title: '', description: deviceSnapshot(detail), publish: 2 } });
      } catch {
        noteFailed = true;
      }
    }

    setSubmitting(false);
    onDone();
    const open = id != null ? { label: 'Open ticket', onClick: () => navigate(ticketPath(id!)) } : undefined;
    if (noteFailed) {
      toast.warning('Ticket created, but the device snapshot note failed', { description: 'Add the device details from the ticket page.', action: open });
    } else {
      toast.success('Ticket created', { description: `Logged against ${detail.device.hostname}`, action: open });
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 px-6 pb-6">
      {matchFailed && (
        <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-warning">
          Couldn't look up the client details for this device. Fill them in below.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Company" required>
          {ctx.company && sameCompany ? (
            <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-muted/30 px-3 text-sm">
              <span className="truncate">{company?.name}</span>
              <button type="button" className="shrink-0 text-xs text-muted-foreground hover:text-foreground" onClick={() => changeCompany(null)}>
                Change
              </button>
            </div>
          ) : (
            <>
              <CompanySearch value={company} onChange={changeCompany} />
              {!ctx.company && (
                <p className="mt-1 text-xs text-warning">This device's Datto site isn't linked to an Autotask company.</p>
              )}
            </>
          )}
        </Row>

        <Row label="Contact">
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className={SELECT_CLS} disabled={!company || contacts.isLoading}>
            <option value="">{contacts.isLoading ? 'Loading contacts…' : '— No contact —'}</option>
            {contacts.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            {sameCompany && ctx.contact && contactId === String(ctx.contact.id) ? (
              <>Matched via {ctx.contact.matchedBy}{ctx.contact.email && <> · {ctx.contact.email}</>}</>
            ) : selectedContact?.emailAddress ? (
              selectedContact.emailAddress
            ) : company && !contactId ? (
              <span className="text-warning">No contact matched{detail.device.lastLoggedInUser && <> · last user {detail.device.lastLoggedInUser}</>}</span>
            ) : null}
          </p>
          {sameCompany && ctx.contact?.matchedBy === 'configuration item' && detail.device.lastLoggedInUser && contactId === String(ctx.contact.id) && (
            <p className="text-xs text-muted-foreground">Last logged-in user: {detail.device.lastLoggedInUser}</p>
          )}
        </Row>
      </div>

      <Row label="Configuration item">
        {ci ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={linkCi} onChange={(e) => setLinkCi(e.target.checked)} className="h-4 w-4" />
            Link to <span className="font-medium">{ci.name}</span>
            <span className="text-xs text-muted-foreground">(CI #{ci.id})</span>
          </label>
        ) : (
          <p className="text-sm text-muted-foreground">
            {company && !sameCompany ? 'Not linked: company changed from the device\'s client.' : 'No Autotask configuration item found for this device.'}
          </p>
        )}
      </Row>

      <Row label="Title" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`e.g. ${detail.device.hostname}: …`} autoFocus />
      </Row>

      <Row label="Description">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="What's wrong? (can be visible to the customer)" />
        <p className="mt-1 text-xs text-muted-foreground">Device details are added automatically as an internal note.</p>
      </Row>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Priority" required>
          <select value={effectivePriority} onChange={(e) => setPriority(e.target.value)} className={SELECT_CLS}>
            {pl?.priority.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
        <Row label="Queue">
          <select value={queueId} onChange={(e) => setQueueId(e.target.value)} className={SELECT_CLS}>
            {(pl?.queueID ?? [{ value: String(SUPPORT_QUEUE_ID), label: 'Support' }]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
        <Row label="Issue type">
          <select value={issueType} onChange={(e) => { setIssueType(e.target.value); setSubIssueType(''); }} className={SELECT_CLS}>
            <option value="">—</option>
            {pl?.issueType.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
        <Row label="Sub-issue type">
          <select value={subIssueType} onChange={(e) => setSubIssueType(e.target.value)} className={SELECT_CLS} disabled={!issueType || subIssueOpts.length === 0}>
            <option value="">—</option>
            {subIssueOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} className="h-4 w-4" />
          Assign to me
        </label>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Incident · Agent · New</span>
          <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>Cancel</Button>
          <Button type="submit" disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Create ticket
          </Button>
        </div>
      </div>
    </form>
  );
}

function Row({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </div>
      {children}
    </div>
  );
}

/** Plain-text device snapshot for the internal note (Autotask doesn't render Markdown). */
function deviceSnapshot({ device: d, alerts, audit }: RmmDeviceDetail): string {
  const patch = patchStatusMeta(d.patchStatus);
  const av = avStatusMeta(d.antivirusStatus);
  const hw = audit ?? d.hardware;
  const lines = [
    'Logged from Cosmos RMM - device snapshot',
    '',
    `Device: ${d.hostname}${d.category ? ` (${[d.category, d.type].filter(Boolean).join(' / ')})` : ''}`,
    `Client: ${deviceClientName(d)} · Site: ${d.siteName}`,
    `Status: ${d.online ? 'Online' : `Offline, last seen ${d.lastSeen ? new Date(d.lastSeen).toLocaleString() : 'never'}`}`,
    d.lastLoggedInUser && `Last user: ${d.lastLoggedInUser}`,
    d.operatingSystem && `OS: ${d.operatingSystem}`,
    (d.intIpAddress || d.extIpAddress) && `IP: ${d.intIpAddress ?? '-'} (internal) · ${d.extIpAddress ?? '-'} (external)`,
    hw && (hw.manufacturer || hw.model || hw.serialNumber) &&
      `Hardware: ${[hw.manufacturer, hw.model].filter(Boolean).join(' ')}${hw.serialNumber ? ` · S/N ${hw.serialNumber}` : ''}`,
    patch && `Patching: ${patch.label}${d.patchesApprovedPending ? ` (${d.patchesApprovedPending} approved pending)` : ''}`,
    av && `Antivirus: ${[d.antivirusProduct, av.label].filter(Boolean).join(' - ')}`,
    d.rebootRequired && 'Reboot required: yes',
    `Open alerts: ${alerts.length}`,
    ...alerts.slice(0, 5).map((a) => `  - [${a.priority}] ${a.summary}`),
    '',
    `Datto RMM: ${d.portalUrl}`,
  ];
  return lines.filter((l) => l !== false && l != null && l !== undefined).join('\n');
}
